<script lang="ts">
  import { createEventDispatcher, tick } from 'svelte';
  import { defaultSwipeConfig, resolveSwipeState } from '$lib/domain/blueprint';
  import { selectedTask, tasksStore } from '$lib/stores/tasks';
  import type { Task } from '$lib/domain/types';

  const dispatch = createEventDispatcher<{ revealCapture: undefined }>();

  let newTask = '';
  let project = '';
  let feedback = '';
  let feedbackTone: 'status' | 'error' = 'status';
  let filter: 'all' | 'now' | 'done' = 'all';
  let titleInput: HTMLInputElement;
  let editInput: HTMLInputElement;
  let editingTaskId: string | null = null;
  let editTitle = '';
  let editProject = '';
  let draggingTaskId: string | null = null;
  let taskOverId: string | null = null;
  let openSwipeTaskId: string | null = null;
  let swipeTaskId: string | null = null;
  let swipeStartX = 0;
  let swipeStartY = 0;

  const focusTaskInput = async () => {
    dispatch('revealCapture');
    await tick();
    titleInput?.focus();
  };

  const addTask = async () => {
    if (!newTask.trim()) {
      feedback = 'Add a task title first.';
      feedbackTone = 'error';
      await focusTaskInput();
      return;
    }
    await tasksStore.add(newTask, project.trim() || undefined);
    feedback = 'Task captured.';
    feedbackTone = 'status';
    newTask = '';
    project = '';
    await focusTaskInput();
  };

  const startEditing = async (task: Task) => {
    editingTaskId = task.id;
    editTitle = task.title;
    editProject = task.project ?? '';
    await tick();
    editInput?.focus();
    editInput?.select();
  };

  const cancelEditing = () => {
    editingTaskId = null;
    editTitle = '';
    editProject = '';
  };

  const saveEditing = async () => {
    if (!editingTaskId) return;
    if (!editTitle.trim()) {
      feedback = 'Task title cannot be empty.';
      feedbackTone = 'error';
      await tick();
      editInput?.focus();
      return;
    }
    await tasksStore.edit(editingTaskId, editTitle, editProject);
    feedback = 'Task updated.';
    feedbackTone = 'status';
    cancelEditing();
  };

  const toggleSelectedTask = (task: Task) => {
    tasksStore.select($selectedTask?.id === task.id ? null : task.id);
  };

  const beginTaskDrag = (event: DragEvent, task: Task) => {
    draggingTaskId = task.id;
    taskOverId = task.id;
    event.dataTransfer?.setData('text/plain', task.id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  };

  const finishTaskDrop = async (task: Task) => {
    if (draggingTaskId && draggingTaskId !== task.id) {
      await tasksStore.move(draggingTaskId, task.id);
    }
    draggingTaskId = null;
    taskOverId = null;
  };

  const resetTaskDrag = () => {
    draggingTaskId = null;
    taskOverId = null;
  };

  const isInteractiveSwipeTarget = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    Boolean(target.closest('button, input, label, a, textarea, select, .drag-handle'));

  const beginTaskSwipe = (event: PointerEvent, task: Task) => {
    if (isInteractiveSwipeTarget(event.target)) return;
    swipeTaskId = task.id;
    swipeStartX = event.clientX;
    swipeStartY = event.clientY;
  };

  const finishTaskSwipe = (event: PointerEvent, task: Task) => {
    if (swipeTaskId !== task.id) return;
    const deltaX = event.clientX - swipeStartX;
    const deltaY = event.clientY - swipeStartY;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      openSwipeTaskId = resolveSwipeState(deltaX, defaultSwipeConfig) ? task.id : null;
    }
    swipeTaskId = null;
  };

  const completeTask = async (task: Task) => {
    openSwipeTaskId = null;
    await tasksStore.toggle(task.id);
  };

  const deferTask = async (task: Task) => {
    openSwipeTaskId = null;
    await tasksStore.defer(task.id);
  };

  const removeTask = async (task: Task) => {
    openSwipeTaskId = null;
    await tasksStore.remove(task.id);
  };

  $: openCount = $tasksStore.filter((task) => task.status === 'open').length;
  $: doneCount = $tasksStore.filter((task) => task.status === 'done').length;
  $: visibleTasks = $tasksStore.filter((task: Task) => {
    if (filter === 'done') return task.status === 'done';
    if (filter === 'now') return task.status === 'open';
    return true;
  });
</script>

<div
  id="tasks-tabpanel"
  class="glass-panel task-panel"
  role="tabpanel"
  aria-labelledby="tasks-title"
  data-testid="task-panel"
>
  <div class="panel-header">
    <div>
      <p class="kicker">Tasks</p>
      <h2 id="tasks-title">Today's focus list</h2>
    </div>
    <button
      class="button secondary icon-button"
      aria-label="Focus task title input"
      on:click={focusTaskInput}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
      </svg>
    </button>
  </div>

  <div class="task-tabs" aria-label="Task filters">
    <button
      aria-pressed={filter === 'all'}
      class:active={filter === 'all'}
      on:click={() => (filter = 'all')}
    >
      All <span>{$tasksStore.length}</span>
    </button>
    <button
      aria-pressed={filter === 'now'}
      class:active={filter === 'now'}
      on:click={() => (filter = 'now')}
    >
      Now <span>{openCount}</span>
    </button>
    <button
      aria-pressed={filter === 'done'}
      class:active={filter === 'done'}
      on:click={() => (filter = 'done')}
    >
      Done <span>{doneCount}</span>
    </button>
  </div>

  <form class="panel-section capture-box" on:submit|preventDefault={addTask}>
    <label class="field-stack">
      <span class="kicker">Task title</span>
      <input
        bind:this={titleInput}
        class="input"
        placeholder="Add a task"
        aria-label="Task title"
        data-testid="task-input"
        bind:value={newTask}
      />
    </label>
    <label class="field-stack">
      <span class="kicker">Project</span>
      <input
        class="input"
        placeholder="Project (optional)"
        aria-label="Project"
        bind:value={project}
      />
    </label>
    <button
      class="button add-task-button"
      class:compact={openCount > 0 || doneCount > 0}
      type="submit"
      data-testid="add-task"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
      </svg>
      Add Task
    </button>
    {#if feedback}
      <div
        class="notice"
        class:danger-text={feedbackTone === 'error'}
        role={feedbackTone === 'error' ? 'alert' : 'status'}
        aria-live={feedbackTone === 'error' ? 'assertive' : 'polite'}
        data-testid="task-feedback"
      >
        {feedback}
      </div>
    {/if}
  </form>

  <div class="task-summary">
    <span>{openCount} open</span>
    <span>{doneCount} done</span>
  </div>

  {#if visibleTasks.length > 0}
    <div class="list task-list reorder-list" role="list">
      {#each visibleTasks as task}
        <div
          class="swipe-row"
          role="listitem"
          aria-label={`Task actions for ${task.title}`}
          data-open={openSwipeTaskId === task.id}
          on:pointerdown={(event) => beginTaskSwipe(event, task)}
          on:pointerup={(event) => finishTaskSwipe(event, task)}
          on:pointercancel={() => (swipeTaskId = null)}
        >
          <div class="swipe-actions" aria-hidden={openSwipeTaskId !== task.id}>
            <button
              class="swipe-action complete"
              type="button"
              tabindex={openSwipeTaskId === task.id ? 0 : -1}
              on:click={() => completeTask(task)}
            >
              {task.status === 'done' ? 'Reopen' : 'Complete'}
            </button>
            <button
              class="swipe-action defer"
              type="button"
              tabindex={openSwipeTaskId === task.id ? 0 : -1}
              on:click={() => deferTask(task)}
            >
              Defer
            </button>
            <button
              class="swipe-action delete"
              type="button"
              tabindex={openSwipeTaskId === task.id ? 0 : -1}
              on:click={() => removeTask(task)}
            >
              Delete
            </button>
          </div>
          <div
            class="task-item reorder-item swipe-content"
            role="group"
            class:selected={$selectedTask?.id === task.id}
            data-dragging={draggingTaskId === task.id}
            data-over={taskOverId === task.id && draggingTaskId !== task.id}
            data-testid="task-item"
            on:dragover|preventDefault={() => (taskOverId = task.id)}
            on:drop|preventDefault={() => finishTaskDrop(task)}
            on:dragend={resetTaskDrag}
          >
            <button
              class="drag-handle"
              type="button"
              draggable="true"
              aria-label={`Reorder ${task.title}`}
              on:dragstart={(event) => beginTaskDrag(event, task)}
              on:dragend={resetTaskDrag}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 7h.01M16 7h.01M8 12h.01M16 12h.01M8 17h.01M16 17h.01" />
              </svg>
            </button>
            <label class="task-check">
              <input
                type="checkbox"
                checked={task.status === 'done'}
                aria-label={`Toggle ${task.title}`}
                on:change={() => tasksStore.toggle(task.id)}
              />
            </label>
            {#if editingTaskId === task.id}
              <div class="task-edit">
                <label class="field-stack">
                  <span class="kicker">Title</span>
                  <input
                    bind:this={editInput}
                    class="input"
                    aria-label={`Edit title for ${task.title}`}
                    bind:value={editTitle}
                    on:keydown={(event) => event.key === 'Enter' && saveEditing()}
                  />
                </label>
                <label class="field-stack">
                  <span class="kicker">Project</span>
                  <input
                    class="input"
                    aria-label={`Edit project for ${task.title}`}
                    bind:value={editProject}
                    on:keydown={(event) => event.key === 'Enter' && saveEditing()}
                  />
                </label>
              </div>
              <div class="task-actions">
                <button class="button small" type="button" on:click={saveEditing}>Save</button>
                <button class="button ghost small" type="button" on:click={cancelEditing}
                  >Cancel</button
                >
              </div>
            {:else}
              <div class="task-title">
                <strong class:done={task.status === 'done'}><bdi>{task.title}</bdi></strong>
                <div class="task-meta">
                  <span class="task-dot" class:done-dot={task.status === 'done'}></span>
                  <span
                    >{task.status === 'done'
                      ? 'Done'
                      : $selectedTask?.id === task.id
                        ? 'Selected'
                        : 'Focus'}</span
                  >
                  {#if task.project}
                    <span class="tag"><bdi>{task.project}</bdi></span>
                  {/if}
                </div>
              </div>
              <div class="task-actions">
                {#if task.status === 'open'}
                  <button
                    class="button secondary small"
                    class:active={$selectedTask?.id === task.id}
                    type="button"
                    aria-pressed={$selectedTask?.id === task.id}
                    aria-label={$selectedTask?.id === task.id
                      ? `Clear focus task ${task.title}`
                      : `Focus ${task.title}`}
                    on:click={() => toggleSelectedTask(task)}
                  >
                    {$selectedTask?.id === task.id ? 'Focused' : 'Focus'}
                  </button>
                {/if}
                <button
                  class="button ghost small"
                  type="button"
                  aria-label={`Edit ${task.title}`}
                  on:click={() => startEditing(task)}>Edit</button
                >
                <button
                  class="button ghost small task-remove"
                  type="button"
                  aria-label={`Remove ${task.title}`}
                  on:click={() => removeTask(task)}>Remove</button
                >
              </div>
            {/if}
          </div>
        </div>
      {/each}
    </div>
  {:else if $tasksStore.length === 0}
    <div class="empty-state">
      <strong>No tasks yet</strong>
      <span>Add a task when you have something to work on.</span>
    </div>
  {:else}
    <div class="empty-state">
      <strong>No tasks in this view</strong>
      <span>Switch filters or capture a new task.</span>
    </div>
  {/if}
</div>
